import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  IconButton,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Stack,
  Grid,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigation } from "contexts/notificationsContext";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";
import {
  IKit,
  ISelectOption,
  KitTabIndex,
} from "./types/kit.types";
import {
  obterKitPorId,
  salvarKit,
  excluirKit,
  duplicarKit,
  listarReferencias,
  listarLinhas,
  listarGrupos,
  listarTiposKit,
  listarCoresKit,
  listarUnidades,
  listarPerfisAtivos,
  listarComponentesAtivos,
  obterPerfilPorId,
  obterComponentePorId,
  validarCodigoUnico,
} from "./services/kitService";
import { useValidacaoCodigoUnico } from "../hooks/useValidacaoCodigoUnico";
import { useSomenteLeituraN1 } from "../hooks/useSomenteLeituraN1";
import { AvisoPlanilhaPendenteN1 } from "../components/AvisoPlanilhaPendenteN1";
import { useFormularioAlterado } from "../hooks/useFormularioAlterado";
import { TabGeral } from "./components/TabGeral";
import { TabDadosAdicionais } from "./components/TabDadosAdicionais";
import { TabComposicao } from "./components/TabComposicao";
import { ModalGerenciamentoMidias } from "./components/ModalGerenciamentoMidias";
import { ModalConfirmacaoExcluir } from "./components/ModalConfirmacaoExcluir";
import { ModalHistorico } from "./components/ModalHistorico";

// FASE 05 — S3/PO 2026-05-13: IdTipoTela do Kit (enum TipoTela = 9).
const ID_TIPO_TELA_KIT = 9;

// Helper para formatar data de atualização
const formatarDataAtualizacao = (data?: string) => {
  if (!data) return "";
  try {
    const date = new Date(data);
    return `${date.toLocaleDateString("pt-BR")} - ${date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
  } catch (e) {
    console.error("Erro ao formatar data:", e);
    return "";
  }
};

export const CadastroKit: React.FC = () => {
  const navigate = useNavigate();
  const { id: idKitParam } = useParams<{ id: string }>();
  const idKit = idKitParam ? parseInt(idKitParam) : undefined;
  const isEdicao = !!idKit;

  const { openNotification } = useNavigation();

  // FASE 05 — 2ª validação/PO 2026-07-24 (B4): empresa "apenas consulta" = somente-leitura.
  // FASE 05 — 6ª validação/PO 2026-08-11: bloqueia edição também quando o MODO EDIÇÃO está desligado.
  // Plano 14/10/2026 (Frente 4): planilha de kits em andamento também deixa a tela somente-leitura.
  const somenteLeitura = useSomenteLeituraN1("kits");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // FASE 05 — Ajustes 25/08 (item 4): SALVAR só habilita depois de alguma alteração.
  // Em cadastro novo não há estado inicial a comparar — o hook devolve sempre habilitado.
  const [tabIndex, setTabIndex] = useState<KitTabIndex>(0);
  const [kit, setKit] = useState<Partial<IKit>>({
    codigo: "",
    descricao: "",
    idSituacao: 1, // Em edição
    idStatus: 1, // Ativo
    idLinha: undefined,
    idGrupo: undefined,
    tipo: undefined,
    medidas: [],
    espessuras: [],
    cores: [],
    composicao: [],
  });

  // Listas de referência
  const [referencias, setReferencias] = useState<ISelectOption[]>([]);
  const [linhas, setLinhas] = useState<ISelectOption[]>([]);
  const [grupos, setGrupos] = useState<ISelectOption[]>([]);
  const [tiposKit, setTiposKit] = useState<ISelectOption[]>([]);
  const [coresKit, setCoresKit] = useState<ISelectOption[]>([]);
  const [unidades, setUnidades] = useState<ISelectOption[]>([]);
  const [perfis, setPerfis] = useState<ISelectOption[]>([]);
  const [componentes, setComponentes] = useState<ISelectOption[]>([]);

  // Modais
  const [modalMidiasAberto, setModalMidiasAberto] = useState(false);
  const [modalExcluirAberto, setModalExcluirAberto] = useState(false);
  const [modalHistoricoAberto, setModalHistoricoAberto] = useState(false);

  // FASE 05 — S3/PO 2026-05-13: carrega listas globais. Referencias filtrada por idTipoTela=9.
  // Linhas e carregada via useEffect quando idReferencia muda.
  const carregarListasReferencia = useCallback(async () => {
    try {
      const resultados = await Promise.allSettled([
        listarReferencias(ID_TIPO_TELA_KIT),
        listarGrupos(),
        listarTiposKit(),
        listarCoresKit(),
        listarUnidades(),
        listarPerfisAtivos(),
        listarComponentesAtivos(),
      ]);

      const refsData = resultados[0].status === 'fulfilled' ? resultados[0].value : [];
      const gruposData = resultados[1].status === 'fulfilled' ? resultados[1].value : [];
      const tiposData = resultados[2].status === 'fulfilled' ? resultados[2].value : [];
      const coresData = resultados[3].status === 'fulfilled' ? resultados[3].value : [];
      const unidadesData = resultados[4].status === 'fulfilled' ? resultados[4].value : [];
      const perfisData = resultados[5].status === 'fulfilled' ? resultados[5].value : [];
      const componentesData = resultados[6].status === 'fulfilled' ? resultados[6].value : [];

      resultados.forEach((result, index) => {
        if (result.status === 'rejected') {
          const nomes = ['Referências', 'Grupos', 'Tipos', 'Cores', 'Unidades', 'Perfis', 'Componentes'];
          console.warn(`Erro ao carregar ${nomes[index]}:`, result.reason);
        }
      });

      setReferencias(refsData);
      setGrupos(gruposData);
      setTiposKit(tiposData);
      setCoresKit(coresData);
      setUnidades(unidadesData);
      setPerfis(perfisData);
      setComponentes(componentesData);
    } catch (error) {
      console.error("Erro ao carregar listas de referência:", error);
      openNotification({
        message: "Erro ao carregar listas de referência.",
        type: "error",
      });
    }
  }, [openNotification]);

  // Carregar dados do kit (se edição)
  const carregarKit = useCallback(async () => {
    if (!idKit) {
      setLoading(false);
      return;
    }

    try {
      const kitData = await obterKitPorId(idKit);
      
      if (kitData) {
        setKit(kitData);
      } else {
        openNotification({ message: "Kit não encontrado", type: "error" });
        navigate("/produtosN1");
      }
    } catch (error) {
      console.error("Erro ao carregar kit:", error);
      openNotification({ message: "Erro ao carregar kit", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [idKit, navigate, openNotification]);

  useEffect(() => {
    carregarListasReferencia();
  }, [carregarListasReferencia]);

  // FASE 05 — S3+/PO 2026-05-13: validacao em tempo real de codigo duplicado.
  const { codigoDuplicado } = useValidacaoCodigoUnico(
    kit.codigo,
    idKit,
    validarCodigoUnico
  );

  // FASE 05 — S3/PO 2026-05-13: cascata Referencia -> Linha.
  useEffect(() => {
    if (kit.idReferencia) {
      listarLinhas(kit.idReferencia).then(setLinhas);
    } else {
      setLinhas([]);
    }
  }, [kit.idReferencia]);

  // FASE 05 — 4ª validação/D6 2026-07-30: o kit carrega direto pelo idKit. A condição
  // anterior (esperar linhas.length > 0) criava um círculo — as linhas só carregam DEPOIS
  // que o kit traz a referência — e a consulta ficava em "processando" para sempre, sem
  // nenhum request sair do browser (provado nos logs IIS de 29/07).
  useEffect(() => {
    carregarKit();
  }, [carregarKit]);

  // FASE 05 — 6ª validação/PO 2026-08-11: quando as unidades carregam, resolve o idUnidade da
  // composição a partir da sigla (UN) devolvida pela API, para o dropdown Unidade voltar preenchido.
  useEffect(() => {
    if (unidades.length === 0) return;
    setKit((prev) => {
      if (!prev.composicao || prev.composicao.length === 0) return prev;
      let mudou = false;
      const composicao = prev.composicao.map((c) => {
        if ((!c.idUnidade || c.idUnidade === 0) && c.unidadeDescricao) {
          // UN vem como sigla (novo) ou descrição (dados antigos) — casa pelos dois.
          const encontrada = unidades.find(
            (u) => u.sigla === c.unidadeDescricao || u.nome === c.unidadeDescricao
          );
          if (encontrada) {
            mudou = true;
            return { ...c, idUnidade: encontrada.id };
          }
        }
        return c;
      });
      return mudou ? { ...prev, composicao } : prev;
    });
  }, [unidades]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: KitTabIndex) => {
    setTabIndex(newValue);
  };

  // FASE 05 — S3/PO 2026-05-13: limpar Linha ao trocar Referencia.
  const handleKitChange = (updates: Partial<IKit>) => {
    setKit((prev) => {
      const next = { ...prev, ...updates };
      if ("idReferencia" in updates && updates.idReferencia !== prev.idReferencia) {
        next.idLinha = undefined;
      }
      return next;
    });
  };

  const { alterado, marcarComoSalvo } = useFormularioAlterado(
    kit,
    { ehNovo: !isEdicao, pronto: !loading }
  );

  const handleSalvar = async () => {
    // Validações básicas
    if (!kit.codigo?.trim() || !kit.descricao?.trim()) {
      openNotification({
        message: "Código e Descrição são obrigatórios.",
        type: "warning",
      });
      return;
    }

    // FASE 05 — S3+/PO 2026-05-13: bloquear save se codigo ja cadastrado.
    if (codigoDuplicado) {
      // FASE 05 — 4ª validação/Frente 9 2026-07-30: mensagem oficial do PO no Salvar
      // (mesmo padrão do Componente); o blur mostra a curta "Código já existe" no campo.
      openNotification({
        message: "Este código de produto já existe. Informe um código diferente ou inclua o sufixo da sua empresa ou entre em contato com o departamento de Produto Informação da Alumisoft.",
        type: "warning",
      });
      return;
    }

    // FASE 05 — 4ª validação/Frente 9 2026-07-30 (decisão P2 do PO): Referência é
    // obrigatória no Kit — validar no salvar, além do asterisco no formulário.
    if (!kit.idReferencia || kit.idReferencia < 1) {
      openNotification({
        message: "Referência é obrigatória.",
        type: "warning",
      });
      return;
    }

    if (!kit.idLinha || kit.idLinha < 1) {
      openNotification({
        message: "Linha é obrigatória.",
        type: "warning",
      });
      return;
    }

    if (!kit.idGrupo || kit.idGrupo < 1) {
      openNotification({
        message: "Grupo é obrigatório.",
        type: "warning",
      });
      return;
    }

    if (!kit.tipo) {
      openNotification({
        message: "Selecione o tipo do kit (Ferragem ou Engenharia).",
        type: "warning",
      });
      return;
    }

    setSaving(true);
    try {
      // FASE 05 — 6ª validação/PO 2026-08-11: o backend (KitComposicaoRequest) espera
      // TipoMaterial/UN/DefVariaveisUsinagem; a tela usa tipoProduto/idUnidade. Sem este
      // mapeamento, TipoMaterial e UN gravavam vazios e a Usinagem (IdKitUsinagem) não gravava.
      const dadosParaSalvar: Partial<IKit> = {
        ...kit,
        composicao: (kit.composicao || []).map((c) => ({
          ...c,
          tipoMaterial: c.tipoProduto,
          // FASE 05 — 6ª validação/PO 2026-08-11: UN grava a SIGLA (PC, BR…), não a descrição.
          un: unidades.find((u) => u.id === c.idUnidade)?.sigla ?? c.unidadeDescricao ?? "",
          defVariaveisUsinagem: c.defVariaveisUsinagem ?? "",
        })) as any,
      };

      const resultado = await salvarKit(dadosParaSalvar);

      if (resultado?.sucesso) {
        // Ajustes 25/08 (item 4): salvou — SALVAR volta a ficar desabilitado até a próxima alteração.
        marcarComoSalvo();
        openNotification({
          message: "Kit salvo com sucesso",
          type: "success",
        });
        
        if (!isEdicao && resultado.id) {
          navigate(`/produtosN1/kits/${resultado.id}`);
        } else if (isEdicao && resultado.id && String(resultado.id) !== String(idKit)) {
          // FASE 05 — 6ª validação/PO 2026-08-11 (F3 Etapa 2): o salvar congelou a versão de
          // mercado e criou uma NOVA cópia de trabalho — navegar para ela (a composição e as
          // medidas já foram gravadas no id retornado pela API).
          navigate(`/produtosN1/kits/${resultado.id}`, { replace: true });
        }
      } else {
        openNotification({
          message: resultado?.mensagem || "Erro ao salvar kit",
          type: "error",
        });
      }
    } catch (error: any) {
      // FASE 05 — 6ª validação/PO 2026-08-04: exibir a mensagem real da API (getErrorsByApi
      // lança), em vez de um texto genérico — o PO passa a ver o motivo da recusa.
      console.error("Erro ao salvar kit:", error);
      openNotification({
        message: error?.message || "Erro ao salvar kit. Tente novamente.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicar = async () => {
    if (!idKit) return;

    try {
      const resultado = await duplicarKit(idKit);
      if (resultado?.sucesso && resultado.id) {
        openNotification({
          message: "Cópia criada. Revise os dados antes de salvar.",
          type: "success",
        });
        navigate(`/produtosN1/kits/${resultado.id}`);
      } else {
        openNotification({
          message: resultado?.mensagem || "Erro ao duplicar kit",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Erro ao duplicar kit:", error);
      openNotification({
        message: "Erro ao duplicar kit",
        type: "error",
      });
    }
  };

  const handleExcluir = async () => {
    if (!idKit) return;

    try {
      const sucesso = await excluirKit(idKit);
      if (sucesso) {
        openNotification({
          message: "Kit excluído com sucesso",
          type: "success",
        });
        navigate("/produtosN1");
      } else {
        openNotification({
          message: "Erro ao excluir kit",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Erro ao excluir kit:", error);
      openNotification({
        message: "Erro ao excluir kit",
        type: "error",
      });
    } finally {
      setModalExcluirAberto(false);
    }
  };

  const handleMidiasSave = (updates: Partial<IKit>, idAlvo?: number) => {
    setKit((prev) => ({ ...prev, ...updates }));
    // Plano 21/09/2026 (Frente 3): kit Disponível → a mídia foi para a cópia de trabalho;
    // navegar para ela (mesmo tratamento do handleSalvar quando o espelho cria a cópia).
    if (idAlvo && String(idAlvo) !== String(idKit)) {
      navigate(`/produtosN1/kits/${idAlvo}`, { replace: true });
    }
  };

  const handleRefreshKit = async () => {
    if (idKit) {
      const kitData = await obterKitPorId(idKit);
      if (kitData) {
        setKit(kitData);
      }
    }
  };

  const handleVoltar = () => {
    navigate("/produtosN1");
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DashboardStackCustom sx={{ width: "100%" }} pt={2}>
      <Grid container display={"flex"} alignItems={"center"}>
        {/* Header: Título + Breadcrumb - Fora do StackCustom para ter cor de fundo da página */}
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5} sx={{ width: "100%", px: 4.5 }}>
          <IconButton onClick={handleVoltar} size="small" sx={{ color: "#666" }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ color: "#333", fontWeight: 400, fontSize: "1.8rem" }}>
            Cadastro de Kit
          </Typography>
        </Stack>
        
        {/* Breadcrumb abaixo do título */}
        <Box sx={{ 
          borderBottom: "2px solid #3C90AD", 
          pb: 1.5, 
          mb: 2,
          ml: 4.5,
          width: "100%",
        }}>
          <Breadcrumbs separator="›" sx={{ mt: 0 }}>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Home
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/produtosN1"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Cadastros
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/produtosN1"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Kits
            </Link>
            <Typography color="text.primary" sx={{ fontSize: "0.75rem" }}>
              {isEdicao ? "Editar" : "Novo Kit"}
            </Typography>
          </Breadcrumbs>
        </Box>

        <StackCustom>
          
          {/* Última atualização e Log */}
          {/* Reporte 02/09 (A8): so isEdicao, como no Componente — exigir dataUltimaAtualizacao escondia o link em registro nunca atualizado */}
          {isEdicao && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2, gap: 2 }}>
              {formatarDataAtualizacao(kit.dataUltimaAtualizacao) && (
                <Typography variant="body2" color="text.secondary">
                  Última atualização: {formatarDataAtualizacao(kit.dataUltimaAtualizacao)}
                </Typography>
              )}
              <Link
                component="button"
                variant="body2"
                onClick={() => setModalHistoricoAberto(true)}
                sx={{ color: "#3C90AD" }}
              >
                Log de atualizações
              </Link>
            </Box>
          )}

          <AvisoPlanilhaPendenteN1 tipoMaterial="kits" />

          {/* Abas */}
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              mb: 3,
              minHeight: "auto",
              "& .MuiTabs-flexContainer": {
                gap: "2px",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                minHeight: "52px",
                backgroundColor: "#7ab8cc",
                color: "#fff",
                fontSize: "0.75rem",
                "&.Mui-selected": {
                  backgroundColor: "#3C90AD",
                  color: "#fff",
                },
              },
            }}
          >
            <Tab label="GERAL" />
            <Tab label="DADOS ADICIONAIS" />
            <Tab label="COMPOSIÇÃO" />
          </Tabs>

      {/* Conteúdo das Abas — bloqueado quando somente-leitura (B4). */}
      <Box sx={{ ...(somenteLeitura && { opacity: 0.7, pointerEvents: "none" }) }}>
      {tabIndex === 0 && (
        <TabGeral
          kit={kit}
          onChange={handleKitChange}
          referencias={referencias}
          linhas={linhas}
          grupos={grupos}
          tiposKit={tiposKit}
          onOpenMidias={() => setModalMidiasAberto(true)}
          codigoDuplicado={codigoDuplicado}
        />
      )}

      {tabIndex === 1 && (
        <TabDadosAdicionais
          kit={kit}
          onChange={handleKitChange}
          coresKit={coresKit}
        />
      )}

      {tabIndex === 2 && (
        <TabComposicao
          kit={kit}
          onChange={handleKitChange}
          unidades={unidades}
        />
      )}
      </Box>
        </StackCustom>

        {/* Rodapé com botões de ação — oculto em somente-leitura (B4). */}
        {!somenteLeitura && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            mt: 3,
            width: "100%",
          }}
        >
          {isEdicao && (
            <>
              <Button
                variant="text"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setModalExcluirAberto(true)}
                disabled={saving}
              >
                EXCLUIR
              </Button>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleDuplicar}
                disabled={saving}
                sx={{ borderColor: "#3C90AD", color: "#3C90AD" }}
              >
                DUPLICAR
              </Button>
            </>
          )}
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSalvar}
            disabled={saving || !alterado}
            sx={{ backgroundColor: "#3C90AD", "&:hover": { backgroundColor: "#2d6f87" } }}
          >
            SALVAR
          </Button>
        </Box>
        )}
      </Grid>

      <ModalGerenciamentoMidias
        open={modalMidiasAberto}
        onClose={() => setModalMidiasAberto(false)}
        kit={kit}
        onSave={handleMidiasSave}
        onRefreshKit={handleRefreshKit}
      />
      <ModalConfirmacaoExcluir
        open={modalExcluirAberto}
        onClose={() => setModalExcluirAberto(false)}
        onConfirm={handleExcluir}
        kitCodigo={kit.codigo || ""}
        kitDescricao={kit.descricao || ""}
      />
      <ModalHistorico
        open={modalHistoricoAberto}
        onClose={() => setModalHistoricoAberto(false)}
        idKit={idKit}
      />
    </DashboardStackCustom>
  );
};
